import { formatIsoDateShort, getCountryName } from '@commenda/utils';
import type { CorporationType, Country } from '@prisma/client';

import {
  computeMembership,
  computePartnership,
  computeShares,
} from './compute';
import { partnerRoleLabel, structureHasGeneralPartner } from './copy';
import { formatMoneyWithCode, isOwnershipValue } from './format';
import type { PartnershipStructure } from './types';

export interface OwnershipSummaryOwner {
  kind?: string;
  companyLegalName?: string;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  nationality?: Country;
  incorporationCountry?: Country;
  jurisdiction?: string;
  entityType?: CorporationType;
}

export interface OwnershipSummaryEntry {
  owner?: OwnershipSummaryOwner;
  ownershipPercent?: number;
  isGeneralPartner?: boolean;
  governanceRole?: string;
  details?: Record<string, unknown>;
}

export interface OwnershipSummaryValue {
  selectedIds?: string[];
  entries?: Record<string, OwnershipSummaryEntry>;
  currency?: string;
  country?: string;
  partnershipStructure?: PartnershipStructure;
  shares?: {
    totalShareCapital?: number;
    authorizedShares?: number;
    issuedShares?: number;
    pricePerShare?: number;
    esopEnabled?: boolean;
    esopPercent?: number;
  };
  membership?: {
    totalShareCapital?: number;
    hasIssuedUnits?: boolean;
    totalUnits?: number;
    managerManaged?: boolean;
  };
  partnership?: { totalShareCapital?: number };
}

export interface OwnershipSummaryMetric {
  label: string;
  value: string;
  caption?: string;
}

export interface OwnershipSummaryOwnerRow {
  id: string;
  name: string;
  kindLabel: string;
  roleLabel: string | null;
  isLeadRole: boolean;
  country: Country | null;
  countryName: string | null;
  identityDetail: string;
  holding: string | null;
  percent: number;
  percentLabel: string;
  capital: string;
  colorClassName: string;
}

export interface OwnershipSummaryColumns {
  owner: string;
  holding: string | null;
  capital: string;
}

export interface OwnershipSummaryTotals {
  holding: string | null;
  percentLabel: string;
  capital: string;
}

export interface OwnershipSummaryModel {
  holdingTypeLabel: string;
  metrics: OwnershipSummaryMetric[];
  columns: OwnershipSummaryColumns;
  owners: OwnershipSummaryOwnerRow[];
  totals: OwnershipSummaryTotals;
  percentTotal: number;
}

interface OwnerFigures {
  holding: (index: number) => string | null;
  capital: (index: number) => number;
  role: (
    entry: OwnershipSummaryEntry
  ) => { label: string; isLead: boolean } | null;
}

interface StructureSummary {
  holdingTypeLabel: string;
  metrics: OwnershipSummaryMetric[];
  columns: OwnershipSummaryColumns;
  figures: OwnerFigures;
  totals: { holding: string | null; capital: number };
}

type JurisdictionLabeler = (jurisdictionId?: string | null) => string;

export class OwnershipSummaryHelper {
  static readonly PRICE_FRACTION_DIGITS = 6;

  static readonly OWNER_COLOR_CLASS_NAMES: readonly string[] = [
    'bg-utility-blue-500',
    'bg-utility-success-500',
    'bg-utility-orange-500',
    'bg-utility-indigo-500',
    'bg-utility-warning-500',
    'bg-utility-error-500',
    'bg-utility-gray-500',
  ];

  static isOwnershipField(field: { value: unknown }): boolean {
    const { value } = field;
    return (
      value != null &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      // Narrowed to a non-array object above.
      isOwnershipValue(value as Record<string, unknown>)
    );
  }

  static formatCount(n: number): string {
    return n.toLocaleString('en-US');
  }

  static formatPercent(n: number): string {
    return `${Number(n.toFixed(2)).toLocaleString('en-US', { maximumFractionDigits: 2 })}%`;
  }

  static formatPrice(currency: string, n: number): string {
    return formatMoneyWithCode(currency, n, {
      maximumFractionDigits: OwnershipSummaryHelper.PRICE_FRACTION_DIGITS,
    });
  }

  static getOwnerName(owner?: Readonly<OwnershipSummaryOwner>): string {
    if (!owner) return 'Owner';
    if (owner.kind === 'entity')
      return owner.companyLegalName?.trim() || 'Entity';
    return (
      [owner.firstName, owner.lastName].filter(Boolean).join(' ').trim() ||
      'Individual'
    );
  }

  // Entities: jurisdiction · entity type. Individuals: date of birth.
  static getIdentityDetail(
    owner: Readonly<OwnershipSummaryOwner> | undefined,
    labelOf: JurisdictionLabeler
  ): string {
    if (!owner) return '';
    if (owner.kind === 'entity') {
      // labelOf echoes the id back when the content doesn't name it.
      const jurisdiction = owner.jurisdiction
        ? labelOf(owner.jurisdiction)
        : '';
      return [
        jurisdiction !== owner.jurisdiction ? jurisdiction : '',
        owner.entityType ?? '',
      ]
        .filter(Boolean)
        .join(' · ');
    }
    const dateOfBirth = formatIsoDateShort(owner.dateOfBirth);
    return dateOfBirth ? `Born ${dateOfBirth}` : '';
  }

  static getOwnerCountry(
    owner?: Readonly<OwnershipSummaryOwner>
  ): Country | null {
    if (!owner) return null;
    return (
      (owner.kind === 'entity'
        ? owner.incorporationCountry
        : owner.nationality) ?? null
    );
  }

  static buildModel(
    value: Readonly<OwnershipSummaryValue>,
    labelOf: JurisdictionLabeler,
    fallbackStructure?: PartnershipStructure
  ): OwnershipSummaryModel {
    const currency = value.currency ?? 'USD';
    const entries = value.entries ?? {};
    const ownerIds = (value.selectedIds ?? []).filter(
      (id) => entries[id] != null
    );
    const ownerEntries = ownerIds.map((id) => entries[id] ?? {});
    const weights = ownerEntries.map((entry) => entry.ownershipPercent ?? 0);
    const structure = OwnershipSummaryHelper.summarizeStructure(
      value,
      weights,
      currency,
      fallbackStructure
    );
    const percentTotal = weights.reduce((sum, w) => sum + Math.max(0, w), 0);

    const owners = ownerEntries.map((entry, i): OwnershipSummaryOwnerRow => {
      const country = OwnershipSummaryHelper.getOwnerCountry(entry.owner);
      const role = structure.figures.role(entry);
      const percent = entry.ownershipPercent ?? 0;
      return {
        id: ownerIds[i] ?? String(i),
        name: OwnershipSummaryHelper.getOwnerName(entry.owner),
        kindLabel: entry.owner?.kind === 'entity' ? 'Entity' : 'Individual',
        roleLabel: role?.label ?? null,
        isLeadRole: role?.isLead ?? false,
        country,
        countryName: country ? (getCountryName(country) ?? null) : null,
        identityDetail: OwnershipSummaryHelper.getIdentityDetail(
          entry.owner,
          labelOf
        ),
        holding: structure.figures.holding(i),
        percent,
        percentLabel:
          entry.ownershipPercent != null
            ? OwnershipSummaryHelper.formatPercent(percent)
            : '—',
        capital: formatMoneyWithCode(currency, structure.figures.capital(i)),
        colorClassName: OwnershipSummaryHelper.getOwnerColorClassName(i),
      };
    });

    return {
      holdingTypeLabel: structure.holdingTypeLabel,
      metrics: structure.metrics,
      columns: structure.columns,
      owners,
      totals: {
        holding: structure.totals.holding,
        percentLabel: OwnershipSummaryHelper.formatPercent(percentTotal),
        capital: formatMoneyWithCode(currency, structure.totals.capital),
      },
      percentTotal,
    };
  }

  static getOwnerColorClassName(index: number): string {
    const palette = OwnershipSummaryHelper.OWNER_COLOR_CLASS_NAMES;
    return palette[index % palette.length] ?? 'bg-utility-gray-500';
  }

  private static summarizeStructure(
    value: Readonly<OwnershipSummaryValue>,
    weights: readonly number[],
    currency: string,
    fallbackStructure?: PartnershipStructure
  ): StructureSummary {
    if (value.membership)
      return OwnershipSummaryHelper.summarizeMembership(
        value.membership,
        weights,
        currency
      );
    if (value.partnership) {
      return OwnershipSummaryHelper.summarizePartnership(
        value.partnership,
        weights,
        currency,
        value.country,
        value.partnershipStructure ?? fallbackStructure
      );
    }
    return OwnershipSummaryHelper.summarizeShares(
      value.shares ?? {},
      weights,
      currency
    );
  }

  private static summarizeShares(
    shares: Readonly<NonNullable<OwnershipSummaryValue['shares']>>,
    weights: readonly number[],
    currency: string
  ): StructureSummary {
    const hasExplicitCapital = shares.totalShareCapital != null;
    const computation = computeShares(shares, weights, {
      shareCapitalEnabled: hasExplicitCapital,
      authorizedVisible: shares.authorizedShares != null,
      esopVisible: !!shares.esopEnabled,
    });
    const hasPrice = hasExplicitCapital || shares.pricePerShare != null;
    const { formatCount, formatPrice } = OwnershipSummaryHelper;

    const metrics: OwnershipSummaryMetric[] = [
      {
        label: 'Authorized shares',
        value: formatCount(computation.authorizedShares),
      },
      { label: 'Issued shares', value: formatCount(computation.issuedShares) },
      {
        label: 'Price per share',
        value: hasPrice
          ? formatPrice(currency, computation.pricePerShare)
          : '—',
        caption: computation.priceIsDerived
          ? 'Share capital ÷ authorized shares'
          : undefined,
      },
      {
        label: 'Share capital',
        value: hasPrice
          ? formatMoneyWithCode(currency, computation.effectiveShareCapital)
          : '—',
        caption:
          hasPrice && !hasExplicitCapital
            ? 'Authorized shares × price per share'
            : undefined,
      },
    ];
    if (computation.esopPoolShares > 0) {
      metrics.push({
        label: 'ESOP pool',
        value: `${formatCount(computation.esopPoolShares)} shares`,
        caption: `${shares.esopPercent ?? 0}% of authorized shares`,
      });
    }

    return {
      holdingTypeLabel: 'Shares (corporation)',
      metrics,
      columns: { owner: 'Shareholder', holding: 'Shares', capital: 'Capital' },
      figures: {
        holding: (i) => formatCount(computation.perOwnerShares[i] ?? 0),
        capital: (i) => computation.perOwnerCapital[i] ?? 0,
        role: () => null,
      },
      totals: {
        holding: formatCount(computation.issuedOutstanding),
        capital: computation.totalCapital,
      },
    };
  }

  private static summarizeMembership(
    membership: Readonly<NonNullable<OwnershipSummaryValue['membership']>>,
    weights: readonly number[],
    currency: string
  ): StructureSummary {
    const computation = computeMembership(membership, weights);
    const { formatCount, formatPrice } = OwnershipSummaryHelper;

    const metrics: OwnershipSummaryMetric[] = [
      {
        label: 'Total capital',
        value: formatMoneyWithCode(currency, computation.totalShareCapital),
      },
    ];
    if (computation.hasIssuedUnits) {
      metrics.push(
        { label: 'Total units', value: formatCount(computation.totalUnits) },
        {
          label: 'Price per unit',
          value: formatPrice(currency, computation.pricePerUnit),
          caption: 'Total capital ÷ total units',
        }
      );
    }
    metrics.push({
      label: 'Management',
      value: computation.managerManaged ? 'Manager-managed' : 'Member-managed',
    });

    return {
      holdingTypeLabel: 'Membership (LLC)',
      metrics,
      columns: {
        owner: 'Member',
        holding: computation.hasIssuedUnits ? 'Units' : null,
        capital: 'Capital',
      },
      figures: {
        holding: (i) =>
          computation.hasIssuedUnits
            ? formatCount(computation.perOwnerUnits[i] ?? 0)
            : null,
        capital: (i) => computation.perOwnerCapital[i] ?? 0,
        role: (entry) =>
          computation.managerManaged
            ? entry.governanceRole === 'MANAGER'
              ? { label: 'Manager', isLead: true }
              : { label: 'Member', isLead: false }
            : null,
      },
      totals: {
        holding: computation.hasIssuedUnits
          ? formatCount(computation.unitsOutstanding)
          : null,
        capital: computation.totalCapitalAllocated,
      },
    };
  }

  private static summarizePartnership(
    partnership: Readonly<NonNullable<OwnershipSummaryValue['partnership']>>,
    weights: readonly number[],
    currency: string,
    country: string | undefined,
    structure: PartnershipStructure | undefined
  ): StructureSummary {
    const computation = computePartnership(partnership, weights);
    const gpApplies = structureHasGeneralPartner(structure);

    return {
      holdingTypeLabel: 'Partnership',
      metrics: [
        {
          label: 'Total capital',
          value: formatMoneyWithCode(currency, computation.totalShareCapital),
        },
        {
          label: 'Partners',
          value: OwnershipSummaryHelper.formatCount(weights.length),
        },
        {
          label: 'Structure',
          value: gpApplies
            ? 'General and limited partners'
            : 'Limited partners only',
        },
      ],
      columns: { owner: 'Partner', holding: null, capital: 'Contribution' },
      figures: {
        holding: () => null,
        capital: (i) => computation.perOwnerCapital[i] ?? 0,
        role: (entry) => {
          const isGp = gpApplies && !!entry.isGeneralPartner;
          return { label: partnerRoleLabel(country, isGp), isLead: isGp };
        },
      },
      totals: { holding: null, capital: computation.totalCapitalAllocated },
    };
  }
}
